import { apiClient, getApiUrl } from '../lib/apiClient';
import type {
  Activity,
  Alert
} from '../types/api';
import {
  extractSingleData,
  type RailsSingleWrappedResponse
} from '../utils/railsResponseHandler';

// Dashboard overview data
export interface DashboardOverview {
  summary: {
    total_students: number;
    present_today: number;
    total_staff: number;
    active_cameras: number;
    total_alerts_today: number;
    unresolved_alerts: number;
  };
  recent_activities: Activity[];
  recent_alerts: Alert[];
  system_status: {
    status: 'healthy' | 'warning' | 'critical';
    message: string;
    last_check: string;
  };
}

// Dashboard statistics
export interface DashboardStats {
  attendance: {
    today: number;
    week_average: number;
    month_average: number;
    trend: 'up' | 'down' | 'stable';
  };
  safety: {
    incidents_today: number;
    incidents_week: number;
    safety_score: number;
    high_risk_areas: Array<{
      area_id: string;
      area_name: string;
      risk_level: 'low' | 'medium' | 'high';
      incident_count: number;
    }>;
  };
  activities: {
    total_today: number;
    by_type: Record<string, number>;
    peak_hours: Array<{
      hour: number;
      count: number;
    }>;
  };
  cameras: {
    total: number;
    online: number;
    offline: number;
    recording: number;
  };
}

// Activity feed item
export interface ActivityFeedItem {
  id: string;
  timestamp: string;
  type: 'detection' | 'alert' | 'system' | 'user_action';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  title: string;
  description: string;
  metadata?: Record<string, any>;
  actor?: {
    id: string;
    name: string;
    type: 'student' | 'staff' | 'system';
  };
  related_entities?: Array<{
    type: 'student' | 'camera' | 'area' | 'class';
    id: string;
    name: string;
  }>;
}

// Activity feed response
interface ActivityFeedResponse {
  activities: ActivityFeedItem[];
  has_more: boolean;
  next_cursor?: string;
}

// Remove custom Rails response interfaces - we'll use the standard ones

// Get dashboard overview - using real API endpoints
export const getDashboardOverview = async (): Promise<DashboardOverview> => {
  // Since there's no single dashboard endpoint, we'll combine multiple API calls
  // This is a temporary implementation until a dedicated dashboard endpoint is created

  try {
    // Get basic counts from various endpoints
    const [healthResponse] = await Promise.all([
      apiClient.get<any>(getApiUrl('/health')),
      // Note: analysis_results/summary will be used in other functions
    ]);

    // Extract health data
    const healthData = healthResponse.data;

    // Create dashboard overview from available data
    const overview: DashboardOverview = {
      summary: {
        total_students: 0, // Will be populated from students endpoint
        present_today: 0,
        total_staff: 0,
        active_cameras: 0,
        total_alerts_today: 0,
        unresolved_alerts: 0,
      },
      recent_activities: [],
      recent_alerts: [],
      system_status: {
        status: healthData.status === 'up' ? 'healthy' :
                healthData.status === 'degraded' ? 'warning' : 'critical',
        message: `System ${healthData.status}. Database: ${healthData.services?.database?.status || 'unknown'}`,
        last_check: healthData.timestamp || new Date().toISOString(),
      }
    };

    return overview;
  } catch (error) {
    console.error('Error fetching dashboard overview:', error);
    throw error;
  }
};

// Get real-time metrics from analysis results
export const getRealTimeMetrics = async (): Promise<{
  total_frames: number;
  unique_persons: number;
  action_summary: Record<string, number>;
  safety_incidents: number;
  time_range: {
    start: string;
    end: string;
  };
}> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<any>>(
    getApiUrl('/analysis_results/summary')
  );
  return extractSingleData(response.data);
};

// Get dashboard statistics - using real API endpoints
export const getDashboardStats = async (
  params?: {
    period?: 'today' | 'week' | 'month' | 'custom';
    start_date?: string;
    end_date?: string;
    school_id?: string;
  }
): Promise<DashboardStats> => {
  try {
    // Get analysis results summary for statistics
    const response = await apiClient.get<RailsSingleWrappedResponse<any>>(
      getApiUrl('/analysis_results/summary'),
      { params }
    );

    const data = extractSingleData(response.data) as any;

    // Transform the analysis data into dashboard stats format
    const stats: DashboardStats = {
      attendance: {
        today: 0, // Will be populated from attendance endpoints when available
        week_average: 0,
        month_average: 0,
        trend: 'stable',
      },
      safety: {
        incidents_today: data.safety_incidents || 0,
        incidents_week: 0,
        safety_score: data.safety_incidents === 0 ? 100 : Math.max(0, 100 - (data.safety_incidents * 10)),
        high_risk_areas: [],
      },
      activities: {
        total_today: data.total_frames || 0,
        by_type: data.action_summary || {},
        peak_hours: [], // Will be populated when we have hourly data
      },
      cameras: {
        total: 0, // Will be populated from cameras endpoint
        online: 0,
        offline: 0,
        recording: 0,
      },
    };

    return stats;
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

// Get activity feed - using real API endpoints
export const getActivityFeed = async (
  params?: {
    limit?: number;
    cursor?: string;
    types?: string[];
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    start_date?: string;
    end_date?: string;
  }
): Promise<ActivityFeedResponse> => {
  try {
    // Use dashboard activity feed endpoint
    const response = await apiClient.get<any>(
      getApiUrl('/dashboard/activity_feed'),
      { params: { limit: params?.limit || 10 } }
    );

    // The response should be in format: { success: true, data: [...], message: "Success" }
    const activities = response.data?.data || [];

    // Transform dashboard activity feed data into ActivityFeedItem format
    const transformedActivities: ActivityFeedItem[] = activities.map((activity: any) => ({
      id: activity.id,
      type: 'detection',
      title: `${activity.student_name} - ${activity.activity_type}`,
      description: `Activity detected at ${activity.location}`,
      timestamp: activity.detected_at,
      priority: activity.safety_level === 'high' ? 'high' : activity.safety_level === 'medium' ? 'medium' : 'low',
      actor: {
        id: activity.id,
        name: activity.student_name,
        type: 'student',
      },
      related_entities: [
        {
          type: 'area',
          id: activity.id,
          name: activity.location,
        }
      ],
      metadata: {
        action: activity.activity_type,
        confidence: activity.confidence,
        safety_level: activity.safety_level,
      },
    }));

    return {
      activities: transformedActivities,
      has_more: false,
      next_cursor: undefined,
    };
  } catch (error) {
    console.error('Error fetching activity feed:', error);
    throw error;
  }
};
